import React, { useState, useEffect, useCallback } from "react";

import { Table, TableProps } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { SorterResult } from "antd/es/table/interface";

import SessionStore from "../stores/SessionStore";

export type GetPageCallbackFunc = (totalCount: number, rows: object[]) => void;

interface IProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnsType<any>;
  getPage: (
    limit: number,
    offset: number,
    filters: object,
    orderBy: string | void,
    orderByDesc: boolean | void,
    callbackFunc: GetPageCallbackFunc,
  ) => void;
  onRowsSelectChange?: (ids: string[]) => void;
  rowKey: string;
  refreshKey?: unknown;
  // When this value changes, the table jumps back to the first page. Use it for
  // search/filter inputs so the user isn't stranded on a now-empty later page.
  resetPageKey?: unknown;
  noPagination?: boolean;
  // Page size to use when the user has not yet chosen one (defaults to 10).
  defaultPageSize?: number;
  // localStorage key under which this table's page size is remembered. Defaults
  // to the shared "rowsPerPage" key; pass a unique key to give a table its own.
  pageSizeKey?: string;
}

function DataTable(props: IProps) {
  const { getPage, refreshKey, resetPageKey, defaultPageSize, pageSizeKey: pageSizeKeyProp, ...otherProps } = props;
  const pageSizeKey = pageSizeKeyProp ?? "rowsPerPage";
  const [totalCount, setTotalCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(
    SessionStore.getRowsPerPage(pageSizeKey, defaultPageSize),
  );
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Reset to the first page whenever resetPageKey changes (e.g. a new search or
  // filter). Adjusting state during render keeps the reload to a single fetch.
  const [prevResetPageKey, setPrevResetPageKey] = useState<unknown>(resetPageKey);
  if (resetPageKey !== prevResetPageKey) {
    setPrevResetPageKey(resetPageKey);
    setCurrentPage(1);
  }

  const [orderBy, setOrderBy] = useState<string>("");
  const [orderByDesc, setOrderByDesc] = useState<boolean>(false);
  const [filters, setFilters] = useState<object>({});
  const [rows, setRows] = useState<object[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const loadPage = useCallback(
    (page: number, pz: number, filters: object, orderBy?: string | void, orderByDesc?: boolean | void) => {
      setLoading(true);

      props.getPage(pz, (page - 1) * pz, filters, orderBy, orderByDesc, (totalCount: number, rows: object[]) => {
        setTotalCount(totalCount);
        setRows(rows);
        setLoading(false);
      });
    },
    [props, pageSize],
  );

  const onRowsSelectChange = (ids: React.Key[]) => {
    const idss = ids as string[];
    if (props.onRowsSelectChange) {
      props.onRowsSelectChange(idss);
    }
  };

  const onChange: TableProps<object>["onChange"] = (pagination, filters, sorter, _) => {
    let page = pagination.current;
    if (!page) {
      page = currentPage;
    }

    let pz = pagination.pageSize;
    if (!pz) {
      pz = pageSize;
    }
    SessionStore.setRowsPerPage(pz, pageSizeKey);

    let firstSorter: SorterResult<object> | void = undefined;
    if (Array.isArray(sorter)) {
      if (sorter.length) {
        firstSorter = sorter[0];
      }
    } else {
      firstSorter = sorter;
    }
    let sort: string | void = undefined;
    if (firstSorter) {
      if (firstSorter.columnKey) {
        sort = firstSorter.columnKey.toString();
        if (firstSorter.order === "descend") {
          setOrderByDesc(true);
        } else {
          setOrderByDesc(false);
        }
      }
    }
    if (!sort) {
      sort = orderBy;
    }

    setCurrentPage(page);
    setPageSize(pz || 0);
    setOrderBy(sort);
    setFilters(filters);
  };

  useEffect(() => {
    loadPage(currentPage, pageSize, filters, orderBy, orderByDesc);
  }, [props, currentPage, pageSize, filters, orderBy, orderByDesc, loadPage]);

  let loadingProps = undefined;
  if (loading) {
    loadingProps = {
      delay: 300,
    };
  }

  let pagination = undefined;
  if (props.noPagination === undefined || props.noPagination === false) {
    pagination = {
      current: currentPage,
      total: totalCount,
      pageSize: pageSize,
      showSizeChanger: true,
    };
  }

  let rowSelection = undefined;
  if (props.onRowsSelectChange) {
    rowSelection = {
      onChange: onRowsSelectChange,
    };
  }

  return (
    <Table
      loading={loadingProps}
      dataSource={rows}
      pagination={pagination || false}
      rowSelection={rowSelection}
      onChange={onChange}
      {...otherProps}
    />
  );
}

export default DataTable;
